import React, { useState, useRef, useEffect } from 'react';
import { useField } from 'formik';
import { Flex, InputGroup, InputLeftElement, Icon } from '@chakra-ui/react';
import {
  CustomDateHeader,
  CustomTimeSelector,
  TimeZoneSelectorV1,
} from './components';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { editMe } from '@/store/me/actions';
import { selectMeForUpdate } from '@/store/me';
import { format, parse, isValid } from 'date-fns';
import { selectUser } from '@/store/bootstrap/selectors';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import './customDateField.css';
import './DateFieldStyles.css';
import { getDefaultTimeZone } from './helpers';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Calendar } from '@kula-ai/icons/dual-tone/Calendar';

const DATEPICKER_PORTAL_ID = 'datepicker-portal';

const ensureDatepickerPortal = () => {
  if (!document.getElementById(DATEPICKER_PORTAL_ID)) {
    const portalDiv = document.createElement('div');
    portalDiv.id = DATEPICKER_PORTAL_ID;
    portalDiv.className = 'custom-ats-datepicker-wrapper';
    document.body.appendChild(portalDiv);
  }
};

interface DateFieldProps {
  item: any;
  field: any;
  formik: any;
}

interface CombinedDateTime {
  selectedDate: Date | null;
  timeValue: string | null;
  timeZone: { label: string; key: string };
}

const normalizeDate = (
  value: string | Date | null,
  sendDateInUTC: boolean = false
): Date | null => {
  if (!value) return null;

  if (sendDateInUTC) {
    // For UTC normalization, handle both numeric timestamps and date strings
    let m;
    if (
      typeof value === 'number' ||
      (typeof value === 'string' && !isNaN(Number(value)))
    ) {
      // Handle numeric timestamps
      m = moment.utc(Number(value));
    } else {
      // Handle date strings
      m = moment.utc(value);
    }
    if (!m.isValid()) return null;

    // Create a Date object that represents the UTC date in local timezone
    // This ensures the DatePicker shows the correct calendar date
    const utcDate = m.startOf('day').toDate();
    const localDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      0,
      0,
      0,
      0
    );

    return localDate;
  }

  // Use moment to parse and normalize the date
  const m = moment(value);

  // Check if it's a valid date
  if (!m.isValid()) return null;
  // Set time to start of day (midnight) and return as Date object
  // This ensures consistent date handling without timezone shifts
  return m.startOf('day').toDate();
};

const separateDateTime = (
  dateTimeString: string | null,
  time_zones: object
) => {
  if (!dateTimeString || typeof dateTimeString !== 'string') {
    return { date: null, time: null, timeZone: null };
  }

  const [date, timeWithOffset] = dateTimeString.split('T');
  if (!date || !timeWithOffset) {
    return { date: null, time: null, timeZone: null };
  }

  const [time] = timeWithOffset.split('+');
  const timeZone = '+' + (timeWithOffset.split('+')[1] || '');
  const [hours, minutes] = time.split(':') || [];

  let timeZoneStr;
  if (time_zones && timeZone?.length > 1) {
    timeZoneStr = Object?.keys?.(time_zones)?.find?.(
      (key) => time_zones?.[key] === timeZone
    );
  }

  return {
    date,
    time: hours && minutes ? `${hours}:${minutes}` : null,
    timeZone: timeZone.length > 1 ? (timeZoneStr ? timeZoneStr : null) : null,
  };
};

export const DateField: React.FC<DateFieldProps> = ({
  item,
  field,
  formik,
}) => {
  const {
    name,
    // label,
    isRequired,
    initialValue,
    className,
    disabled = false,
    isInline = false,
    enableTime = false,
    // placeholder,
    disablePreviousDate = false,
    dateFormat,
    // dateType = 'd-MMMM-yyyy',
    style,
    returnWithTimeZoneFormat = false,
    customHeader = CustomDateHeader,
    customOnChange = () => {},
    styleVersion = 'v1',
    sendDateInUTC = false,
    disableValidation = false,
  } = item;

  const [, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers || {};
  // @ts-expect-error - time_zones is not typed
  const { time_zones = {} } = useSelector(selectMeForUpdate) || {};
  const { error, touched, value } = meta;
  const { submitCount } = formik || {};

  const { time: initialTime, timeZone: initialTimeZone } = separateDateTime(
    value,
    time_zones
  );
  const currUser = useSelector(selectUser);
  const { time_fmt, date_fmt, time_zone: userTimeZone } = currUser || {};
  const { dateFnsFormat } = useDateFormat();

  const getTimeFormat = () => {
    return time_fmt === 'hour_12' ? 'hh:mm A' : 'HH:mm';
  };

  // Combine state into a single object
  const [combinedDateTime, setCombinedDateTime] = useState<CombinedDateTime>({
    selectedDate: value
      ? normalizeDate(value, sendDateInUTC)
      : initialValue
        ? normalizeDate(initialValue, sendDateInUTC)
        : null,
    timeValue: initialTime || null,
    timeZone: initialTimeZone
      ? { label: initialTimeZone, key: initialTimeZone }
      : getDefaultTimeZone(userTimeZone),
  });

  const previousDate = useRef<Date | undefined>(combinedDateTime.selectedDate);
  const dispatch = useDispatch();

  useEffect(() => {
    ensureDatepickerPortal();
  }, []);

  useEffect(() => {
    if (enableTime) {
      dispatch(editMe({}));
    }
  }, [dispatch, enableTime]);

  // New useEffect to handle changes in the value from Formik
  useEffect(() => {
    if (value && !combinedDateTime.selectedDate) {
      const { time: initialTime, timeZone: initialTimeZone } = separateDateTime(
        value,
        time_zones
      );
      setCombinedDateTime({
        selectedDate: normalizeDate(value, sendDateInUTC),
        timeValue: initialTime || null,
        timeZone: initialTimeZone
          ? { label: initialTimeZone, key: initialTimeZone }
          : getDefaultTimeZone(userTimeZone),
      });
    }
  }, [value, time_zones, combinedDateTime.selectedDate, userTimeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (date: Date) => {
    if (
      previousDate?.current &&
      previousDate?.current?.toDateString() === date?.toDateString()
    ) {
      return;
    }
    previousDate.current = date;
    setCombinedDateTime((prev) => ({
      ...prev,
      selectedDate: date,
      timeValue: null,
    })); // Reset timeValue on date change
  };

  const handleTimeChange = (time: string) => {
    try {
      let parsedDate;

      // First, try parsing with the expected format
      parsedDate = parse(time, getTimeFormat(), new Date());

      // If that fails, try parsing with a simple 'HH:mm' format
      if (!isValid(parsedDate)) {
        parsedDate = parse(time, 'HH:mm', new Date());
      }

      // If it's still not valid, log an error and return
      if (!isValid(parsedDate)) {
        console.error('Invalid date parsed:', time);
        return;
      }

      const time24 = format(parsedDate, 'HH:mm');
      setCombinedDateTime((prev) => ({ ...prev, timeValue: time24 }));
    } catch (error) {
      console.error('Error parsing time:', error);
      // Handle the error appropriately (e.g., show an error message to the user)
    }
  };

  const handleTimeZoneChange = (zone: { label: string; key: string }) => {
    setCombinedDateTime((prev) => ({ ...prev, timeZone: zone }));
  };

  const updateValueWithTimeZone = (combined: CombinedDateTime) => {
    const { selectedDate, timeValue, timeZone } = combined;
    if (!selectedDate) return;

    let latestValue;

    // For date-only fields (no time), don't apply timezone conversion
    // This prevents timezone shifts for fields like date of birth
    if (!enableTime) {
      // For date-only fields, normalize to UTC midnight to prevent timezone shifts
      // Use moment to properly handle the date without timezone issues
      const normalizedDate = sendDateInUTC
        ? moment
            .utc([
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              selectedDate.getDate(),
              0,
              0,
              0,
            ])
            .toDate()
        : selectedDate;

      if (dateFormat) {
        latestValue = dateFormat(normalizedDate);
      } else {
        latestValue = normalizedDate;
      }
    } else {
      // For date+time fields, create date in the target timezone
      // This prevents date shifts when the timezone differs from UTC
      const dateString = moment(selectedDate).format('YYYY-MM-DD');
      let adjustedMoment = moment.tz(dateString, timeZone.key);

      if (timeValue) {
        const [hours, minutes] = timeValue.split(':').map(Number);
        adjustedMoment = adjustedMoment
          .hours(hours)
          .minutes(minutes)
          .seconds(0);
      }

      latestValue = returnWithTimeZoneFormat
        ? adjustedMoment.format('YYYY-MM-DDTHH:mm:ssZ')
        : dateFormat
          ? dateFormat(adjustedMoment.toDate())
          : adjustedMoment.toDate();
    }

    setValue(latestValue);
    customOnChange(combined);
    setTouched(true);
    setError(null);
  };

  useEffect(() => {
    updateValueWithTimeZone(combinedDateTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedDateTime]); // Only depend on the combined state

  const showError =
    !disableValidation && (submitCount > 0 || touched) && !value && isRequired;

  // Get the appropriate wrapper className based on feature flag
  const getWrapperClassName = () => {
    const baseClass = 'custom-ats-datepicker-wrapper';
    const newDateClass = styleVersion === 'v2' ? 'new-date' : '';
    const userClass = className || '';

    return `${baseClass} ${newDateClass} ${userClass}`.trim();
  };

  return (
    <div className={getWrapperClassName()}>
      <InputGroup>
        {styleVersion === 'v2' && (
          <InputLeftElement
            pointerEvents="none"
            height="100%"
            borderWidth="1px"
            borderLeftRadius="6px"
            borderColor={showError ? 'error-200' : 'border-color'}
            marginRight="8px"
            backgroundColor="secondary-25"
          >
            <Icon as={Calendar} />
          </InputLeftElement>
        )}
        <DatePicker
          id={name}
          name={name}
          style={style}
          required={isRequired}
          dateFormat={dateFnsFormat}
          inline={isInline}
          disabled={disabled}
          errors={error}
          touched={touched}
          onBlur={field?.onBlur}
          selected={combinedDateTime?.selectedDate}
          minDate={
            disablePreviousDate === true ? new Date() : disablePreviousDate
          }
          onChange={handleDateChange}
          showTimeInput={enableTime}
          placeholderText={date_fmt}
          className={`ats-date-input-field ${
            showError
              ? styleVersion === 'v2'
                ? 'error-date-field-v2'
                : 'error-date-field'
              : ''
          } ${styleVersion === 'v2' ? 'with-icon' : ''} ${className || ''}`}
          renderCustomHeader={customHeader}
          // note: this is for using default date field time
          // timeFormat={getTimeFormat()}
          // showTimeSelect={enableTime}
          // timeIntervals={15}
          customTimeInput={<></>}
          calendarStartDay={1}
          autoComplete="off"
          showPopperArrow={false}
          portalId={DATEPICKER_PORTAL_ID}
        />
      </InputGroup>
      {enableTime && (
        <Flex flexDir="column" gap="6px">
          <CustomTimeSelector
            date={combinedDateTime.selectedDate}
            value={combinedDateTime.timeValue}
            onChange={handleTimeChange}
            timeFormat={getTimeFormat()}
          />
          <TimeZoneSelectorV1
            value={combinedDateTime.timeZone}
            onChange={handleTimeZoneChange}
          />
        </Flex>
      )}
    </div>
  );
};
